/**
 * Audit helper: logs every CPT-level edit (add/remove procedure, add/remove modifier)
 * to the case_cpt_edit_log table for immutable tracking.
 */

import { supabase } from '@/integrations/supabase/client';
import { Json } from '@/integrations/supabase/types';

export type CptEditAction = 'add_procedure' | 'remove_procedure' | 'add_modifier' | 'remove_modifier';
export type CptEditScope = 'case_only' | 'master_template';

export interface CptEditLogEntry {
  caseId: string;
  action: CptEditAction;
  cptCode: string;
  modifierCode?: string;
  section?: 'primary' | 'secondary';
  scope: CptEditScope;
  templateId?: string;
  templateVersionBefore?: number;
  templateVersionAfter?: number;
  details?: Json;
}

/**
 * Write a CPT edit event to the audit log.
 * Silently catches errors to avoid blocking the user flow.
 */
export async function logCptEdit(entry: CptEditLogEntry): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    await supabase.from('case_cpt_edit_log').insert([{
      case_id: entry.caseId,
      actor_id: user.id,
      action: entry.action,
      cpt_code: entry.cptCode,
      modifier_code: entry.modifierCode ?? null,
      section: entry.section ?? null,
      scope: entry.scope,
      template_id: entry.templateId ?? null,
      template_version_before: entry.templateVersionBefore ?? null,
      template_version_after: entry.templateVersionAfter ?? null,
      details: entry.details ?? null,
    }]);
  } catch (err) {
    console.error('[audit] Failed to log CPT edit:', err);
  }
}
